export enum Role {
  Tank = 0,
  Damage = 1,
  Support = 2,
}

export enum HeroId {
  CaptainAmericano = 0,
  DoctorEstranged = 1,
  Root = 2,
  Bulk = 3,
  Magnetron = 4,
  PennyParka = 5,
  Thud = 6,
  Venison = 7,
  BlackPanthera = 8,
  BlackWindow = 9,
  Squawkeye = 10,
  Hella = 11,
  IronyFist = 12,
  IonMan = 13,
  Tragik = 14,
  NoonKnight = 15,
  SubMarinara = 16,
  Psyclone = 17,
  ScarletTwitch = 18,
  CiderMan = 19,
  SquirrelyGirl = 20,
  StarLorde = 21,
  Brainstorm = 22,
  Punnisher = 23,
  WinterShoulder = 24,
  Woolverine = 25,
  AtomWarlock = 26,
  CloakSwagger = 27,
  Geoff = 28,
  Lowkey = 29,
  LunaSnowcone = 30,
  Mantissa = 31,
  PocketRaccoon = 32,
}

export const HERO_COUNT = 33;
export const ROLE_NAMES: Record<Role, string> = {
  [Role.Tank]: "Vanguard",
  [Role.Damage]: "Duelist",
  [Role.Support]: "Strategist",
};

/**
 * Ult energy: the share of damage and healing dealt that becomes energy, plus energy per second
 * while a round is in progress. Rivals' Season 10 role defaults; a hero or form can override.
 */
export interface UltGain {
  damage?: number;
  heal?: number;
  passive?: number;
}
export const ULT_GAIN: Record<Role, Required<UltGain>> = {
  [Role.Tank]: { damage: 0.55, heal: 0.55, passive: 11 },
  [Role.Damage]: { damage: 0.55, heal: 0.55, passive: 11 },
  [Role.Support]: { damage: 0.5, heal: 0.65, passive: 8 },
};

/** Ability input slots. */
export enum Slot {
  Primary = 0,
  Secondary = 1,
  Ability1 = 2,
  Ability2 = 3,
  Ultimate = 4,
  Ability3 = 5,
}
export const SLOT_COUNT = 6;

export interface HitCapsule {
  /** Offsets from feet position (y up). */
  a: number;
  b: number;
  r: number;
  head: boolean;
}

export interface AbilityInfo {
  name: string;
  key: string;
  description: string;
  cooldown: number; // seconds (0 = none)
  charges?: number;
}

export type AbilityMap = { [Slot.Primary]: AbilityInfo; [Slot.Ultimate]: AbilityInfo } & Partial<Record<Slot, AbilityInfo>>;

/** One of a hero's switchable weapons: its own magazine and reload. */
export interface WeaponInfo {
  name: string;
  ammo: number;
  reloadTime: number;
}

/** Seconds before a hero can fire after switching weapons. */
export const WEAPON_SWAP_SECONDS = 0.25;

export interface HeroForm {
  name?: string;
  color?: string;
  height?: number;
  radius?: number;
  headRadius?: number;
  speed?: number;
  jumpSpeed?: number;
  health?: number;
  armor?: number;
  shields?: number;
  ammo?: number;
  reloadTime?: number;
  ultCost?: number;
  ultGain?: UltGain;
  passive?: HeroDef["passive"];
  passives?: HeroDef["passives"];
  /** Per-slot overrides merged over the base form; null hides the base slot in this form. */
  abilities?: Partial<Record<Slot, AbilityInfo | null>>;
}

export interface HeroDef {
  id: HeroId;
  slug: string;
  name: string;
  role: Role;
  color: string;
  blurb: string;
  // Health pools
  health: number;
  armor: number;
  /** Rivals' Regenerative Shield: refills over 5 s after 5 s without taking damage. */
  shields: number;
  // Body
  radius: number;
  height: number;
  headRadius: number;
  // Movement
  speed: number;
  jumpSpeed: number;
  // Weapon basics
  ammo: number; // 0 = no ammo
  reloadTime: number; // seconds
  /**
   * Two weapons switched with 1 / 2 and the mouse wheel, each keeping its own magazine.
   * weapons[0] is the one a life starts with and must match `ammo` / `reloadTime`.
   */
  weapons?: [WeaponInfo, WeaponInfo];
  ultCost: number; // ult energy (see ULT_GAIN)
  /** Overrides the role's ULT_GAIN rates. */
  ultGain?: UltGain;
  abilities: AbilityMap;
  passive?: { name: string; description: string };
  passives?: { name: string; description: string }[];
  /** Optional alternate forms. Array index 0 is form 1; form 0 is this base def. */
  forms?: HeroForm[];
}

/** The magazine and reload of the weapon in hand (`slot` = c.Weapon.slot). */
export const heldWeapon = (def: HeroDef, slot: number): { ammo: number; reloadTime: number } => def.weapons?.[slot] ?? def;

export function abilityInfo(def: HeroDef, slot: Slot): AbilityInfo | undefined {
  return def.abilities[slot];
}

export function heroCapsules(def: HeroDef, crouched: boolean): HitCapsule[] {
  const h = crouched ? def.height * 0.7 : def.height;
  const headY = h - def.headRadius;
  return [
    { a: def.radius, b: Math.max(def.radius, headY - def.headRadius - def.radius * 0.6), r: def.radius, head: false },
    { a: headY, b: headY, r: def.headRadius, head: true },
  ];
}

/** Standing eye height for camera/aim origin. */
export const eyeHeight = (def: HeroDef, crouched: boolean) => (crouched ? def.height * 0.7 : def.height) - def.headRadius;
